import { AppState } from "../types";
import type { OpportunityRecommendation, ConfidenceLevel } from "../types/intelligence";
import {
  getOpportunityCandidateStory,
  getOpportunityCheckpoints,
  getOpportunityDMs,
  getOpportunityProof,
  getOpportunityTasks,
} from "../workflow";

export type { OpportunityRecommendation };

function confidenceFromCompleteness(coverage: number): ConfidenceLevel {
  if (coverage >= 0.75) {
    return "high";
  }
  if (coverage >= 0.4) {
    return "medium";
  }
  return "low";
}

export function buildOpportunityRecommendation(
  state: AppState,
  opportunityId: string,
): OpportunityRecommendation {
  const proof = getOpportunityProof(state, opportunityId);
  const dms = getOpportunityDMs(state, opportunityId);
  const tasks = getOpportunityTasks(state, opportunityId);
  const checkpoints = getOpportunityCheckpoints(state, opportunityId);
  const story = getOpportunityCandidateStory(state, opportunityId);

  const missingEvidence: string[] = [];
  const automationCandidates: string[] = [];

  if (proof.length < 2) {
    missingEvidence.push("Need at least two proof records for stable story quality.");
  }
  if (dms.length === 0) {
    missingEvidence.push("No correspondence captured yet for outreach readiness.");
  }
  if (!story) {
    missingEvidence.push("Candidate story not generated.");
  }
  if (tasks.filter((task) => task.blocking && task.status !== "completed").length > 0) {
    missingEvidence.push("Blocking tasks remain open.");
  }

  if (!story && proof.length > 0) {
    automationCandidates.push("Auto-generate a candidate story draft from proof + correspondence.");
  }
  if (dms.length === 0 && proof.length > 0) {
    automationCandidates.push("Draft first outreach message using current proof signals.");
  }
  if (checkpoints.length === 0) {
    automationCandidates.push("Run AI checkpoint to determine stage readiness and risks.");
  }

  const coverageParts = [proof.length > 0, dms.length > 0, !!story, tasks.length > 0].filter(
    Boolean,
  ).length;
  const coverage = coverageParts / 4;

  if (!story && proof.length > 0) {
    return {
      primaryAction: "Generate candidate story",
      rationale:
        "You have enough raw proof to create a reviewable narrative and reduce manual editing later.",
      confidence: confidenceFromCompleteness(coverage),
      missingEvidence,
      automationCandidates,
    };
  }

  const blockingTasks = tasks.filter((task) => task.blocking && task.status !== "completed");
  if (blockingTasks.length > 0) {
    return {
      primaryAction: "Resolve blocking tasks",
      rationale:
        "Open blockers are the fastest path to stage progression and prevent downstream rework.",
      confidence: confidenceFromCompleteness(coverage),
      missingEvidence,
      automationCandidates,
    };
  }

  if (dms.length === 0) {
    return {
      primaryAction: "Prepare first outreach message",
      rationale: "No communication thread exists yet; outreach unlocks forward momentum.",
      confidence: confidenceFromCompleteness(coverage),
      missingEvidence,
      automationCandidates,
    };
  }

  return {
    primaryAction: "Advance stage with AI checkpoint",
    rationale:
      "Evidence coverage is healthy; run a checkpoint and move to the next workflow stage.",
    confidence: confidenceFromCompleteness(coverage),
    missingEvidence,
    automationCandidates,
  };
}
